import { Component, Input, OnInit, signal, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { ComponentCanDeactivate } from '../../../../core/guards/unsaved-changes.guard';
import { SupplierRegistryService } from '../../../services/supplier-registry.service';
import { SupplierValidatorsService } from '../../../services/supplier-validators.service';
import {
  GeographyService,
  Country,
  Region,
  Province,
  Municipality,
} from '../../../../core/services/geography.service';
import {
  FormDropdownService,
  FormDropdownItem,
} from '../../../../core/services/form-dropdown.service';
import { MessageService, MenuItem, ConfirmationService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ContractsRegistryComponent } from '../../contracts-registry/contracts-registry.component';
import { ConfirmationDialogService } from '../../../../core/services/confirmation-dialog.service';
import { NoSpecialCharsValidator } from '../../../../core/validators/no-special-chars.validator';
import { ValidationErrorComponent } from '../../../../core/shared/validation-error/validation-error.component';
import {
  CollapsibleSectionDirective,
  SectionCollapseToggleComponent,
} from '../../../../core/shared/collapsible-section/collapsible-section';

// PrimeNG Imports (omitted for brevity)
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { Toast } from 'primeng/toast';

// Interface definitions
interface SupplierDetails {
  id?: number;
  codeAlboSOL: string;
  codeSupplierCoupa: string;
  coupaAuthor?: string;
  codeSupplierBravoSolution: string;
  isSupplierManagedOnCoupa: boolean;
}

interface Supplier {
  id?: number;
  supplierName: string;
  supplierTypeLabel: string;
  vatNumber: string;
  vatCountry: string | null;
  fiscalCode?: string;
  primaryEmail?: string;
  certifiedEmail?: string;
  telephoneNumber?: string;
  mobileNumber?: string;
  contactPerson: string;
  address: string;
  alternativeName?: string;
  personType?: string;
  axaBuyer?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'DRAFT' | 'CEASED';
  statusDisplay?: string;
  details: SupplierDetails;
}

@Component({
  selector: 'app-supplier-detail',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    TagModule,
    ProgressSpinnerModule,
    InputTextModule,
    RadioButtonModule,
    SelectModule,
    Toast,
    BreadcrumbModule,
    ConfirmDialogModule,
    ContractsRegistryComponent,
    ValidationErrorComponent,
    CollapsibleSectionDirective,
    SectionCollapseToggleComponent,
  ],
  templateUrl: './supplier-detail.component.html',
  styleUrls: ['./supplier-detail.component.scss'],
})
export class SupplierDetailModalComponent implements OnInit, ComponentCanDeactivate {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private titleService = inject(Title);
  private supplierService = inject(SupplierRegistryService);
  private geographyService = inject(GeographyService);
  private formDropdownService = inject(FormDropdownService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private confirmDialogService = inject(ConfirmationDialogService);
  private fb = inject(FormBuilder);

  isEditModeAnagrafica = signal(false);
  isEditModeDetails = signal(false);
  isLoading = signal(true);
  breadcrumbItems = signal<MenuItem[]>([]);
  supplierId = signal<string | undefined>(undefined);

  // Effect per gestire enable/disable dei campi anagrafica
  constructor() {
    effect(() => {
      const isEdit = this.isEditModeAnagrafica();
      if (isEdit) {
        this.enableAnagraficaFields();
      } else {
        this.disableAnagraficaFields();
      }
    });

    effect(() => {
      const isEdit = this.isEditModeDetails();
      if (isEdit) {
        this.enableDetailsFields();
      } else {
        this.disableDetailsFields();
      }
    });
  }

  // Signals per opzioni dropdown
  personTypeOptions = signal<FormDropdownItem[]>([]);
  supplierTypeOptions = signal<FormDropdownItem[]>([]);
  allSupplierTypeOptions = signal<FormDropdownItem[]>([]); // Tutte le opzioni per filtraggio
  addressTypologyOptions = signal<FormDropdownItem[]>([]);
  countryOptions = signal<Country[]>([]);
  regionOptions = signal<Region[]>([]);
  provinceOptions = signal<Province[]>([]);
  municipalityOptions = signal<Municipality[]>([]);
  vatCountryOptions = signal<FormDropdownItem[]>([]);

  get vatNumberHint(): string {
    return SupplierValidatorsService.vatNumberHint(this.anagraficaForm.get('vatCountry')?.value);
  }

  get vatNumberMaxLength(): number {
    return SupplierValidatorsService.vatNumberMaxLength(this.anagraficaForm.get('vatCountry')?.value);
  }

  // Reactive Forms
  anagraficaForm: FormGroup = this.fb.group({
    supplierName: ['', [Validators.required, NoSpecialCharsValidator('free-text')]],
    supplierTypeId: ['', Validators.required],
    supplierTypeLabel: [''],
    alternativeName: ['', NoSpecialCharsValidator('free-text')],
    personType: ['', Validators.required],
    vatCountry: ['IT', [
      Validators.required,
      SupplierValidatorsService.vatCountryValidator,
      () => this.vatCountryOptions().length ? null : { vatCountriesUnavailable: true },
    ]],
    vatNumber: ['', SupplierValidatorsService.vatNumberValidator],
    fiscalCode: ['', [Validators.required, SupplierValidatorsService.taxCodeValidator, NoSpecialCharsValidator('alphanumeric')]],
    telephoneNumber: ['', [SupplierValidatorsService.phoneNumberValidator, NoSpecialCharsValidator('strict')]],
    mobileNumber: ['', [SupplierValidatorsService.mobileNumberValidator, NoSpecialCharsValidator('strict')]],
    primaryEmail: ['', [Validators.email, NoSpecialCharsValidator('strict')]],
    certifiedEmail: ['', [SupplierValidatorsService.pecEmailValidator, NoSpecialCharsValidator('strict')]],
    contactPerson: ['', NoSpecialCharsValidator('free-text')],
    axaBuyer: ['', [Validators.required, NoSpecialCharsValidator('free-text')]],
    // Campi indirizzo
    addressId: [null], // ID dell'indirizzo esistente
    addressTypology: [''],
    address: ['', NoSpecialCharsValidator('address')],
    addressNumber: ['', NoSpecialCharsValidator('alphanumeric')],
    zipCode: ['', NoSpecialCharsValidator('alphanumeric')],
    country: ['IT'],
    region: [''],
    province: [''],
    municipality: [''],
  });

  detailsForm: FormGroup = this.fb.group({
    codeAlboSOL: ['', NoSpecialCharsValidator('strict')],
    codeSupplierCoupa: ['', NoSpecialCharsValidator('strict')],
    coupaAuthor: ['', NoSpecialCharsValidator('strict')],
    codeSupplierBravoSolution: ['', NoSpecialCharsValidator('strict')],
  });

  // Initialize with a complete default object structure
  supplier = signal<Supplier>({
    supplierName: '',
    supplierTypeLabel: '',
    vatNumber: '',
    vatCountry: 'IT',
    fiscalCode: '',
    primaryEmail: '',
    certifiedEmail: '',
    telephoneNumber: '',
    mobileNumber: '',
    contactPerson: '',
    address: '',
    alternativeName: '',
    details: {
      codeAlboSOL: '',
      codeSupplierCoupa: '',
      coupaAuthor: '',
      codeSupplierBravoSolution: '',
      isSupplierManagedOnCoupa: false,
    },
  });

  ngOnInit(): void {
    this.titleService.setTitle('Dettaglio - PalEasy');

    // Inizializza breadcrumb con valore di default
    this.breadcrumbItems.set([
      { label: 'Registro Fornitori', routerLink: '/axa/suppliers-registry' },
      { label: 'Dettaglio Fornitore' },
    ]);

    // Carica opzioni per i dropdown
    this.loadDropdownOptions();
    this.loadCountries();

    // Setup listeners per cascata geografica
    this.setupAddressListeners();

    // Get supplier ID from route params
    this.route.params.subscribe((params) => {
      const supplierId = params['id'];
      if (supplierId) {
        this.loadSupplierDetails(supplierId);
      } else {
        console.error('No supplier ID provided');
        this.isLoading.set(false);
      }
    });
  }

  private loadSupplierDetails(id: string): void {
    this.isLoading.set(true);
    this.supplierService.getSupplier(id).subscribe({
      next: (response: any) => {
        if (response?.status && response?.data) {
          const apiData = response.data;

          // Map API response to component model
          const mappedSupplier: Supplier = {
            id: apiData.id,
            supplierName: apiData.supplierName || '',
            supplierTypeLabel: this.getSupplierTypeLabel(apiData.supplierTypeId),
            vatNumber: apiData.vatNumber || '',
            vatCountry: apiData.vatCountry ?? null,
            fiscalCode: apiData.fiscalCode || '',
            primaryEmail: apiData.primaryEmail || '',
            certifiedEmail: apiData.certifiedEmail || '',
            telephoneNumber: apiData.telephoneNumber || '',
            mobileNumber: apiData.mobileNumber || '',
            contactPerson: apiData.contactPerson || '',
            address: apiData.addresses?.[0]?.fullAddress || 'N/A',
            alternativeName: apiData.alternativeName || '',
            personType: apiData.personType || '',
            axaBuyer: apiData.axaBuyer || '',
            status: apiData.status,
            statusDisplay: this.getStatusDisplay(apiData.status),
            details: {
              id: apiData.details?.id,
              codeAlboSOL: apiData.details?.codeAlboSOL || '',
              codeSupplierCoupa: apiData.details?.codeSupplierCoupa || '',
              coupaAuthor: apiData.details?.coupaAuthor || '',
              codeSupplierBravoSolution: apiData.details?.codeSupplierBravoSolution || '',
              isSupplierManagedOnCoupa: apiData.details?.isSupplierManagedOnCoupa ?? false,
            },
          };

          this.supplier.set(mappedSupplier);

          // WCAG 9.2.4.2: Update page title with supplier name for better context
          this.titleService.setTitle(`${mappedSupplier.supplierName} - Dettaglio Fornitore - PalEasy`);

          // Popola i form reattivi
          this.anagraficaForm.patchValue(
            {
              supplierName: mappedSupplier.supplierName,
              supplierTypeId: apiData.supplierTypeId || null,
              supplierTypeLabel: mappedSupplier.supplierTypeLabel,
              alternativeName: mappedSupplier.alternativeName || '',
              personType: apiData.personType || '',
              vatCountry: mappedSupplier.vatCountry,
              vatNumber: mappedSupplier.vatNumber,
              fiscalCode: mappedSupplier.fiscalCode,
              telephoneNumber: mappedSupplier.telephoneNumber,
              mobileNumber: mappedSupplier.mobileNumber,
              primaryEmail: mappedSupplier.primaryEmail,
              certifiedEmail: mappedSupplier.certifiedEmail,
              contactPerson: mappedSupplier.contactPerson,
              axaBuyer: apiData.axaBuyer || '',
            },
            { emitEvent: true },
          ); // emitEvent: true per triggerare il filtro dei supplierTypes

          // Popola campi indirizzo se presenti
          if (apiData.addresses && apiData.addresses.length > 0) {
            const addr = apiData.addresses[0];

            // Salva l'ID dell'indirizzo
            this.anagraficaForm.patchValue(
              {
                addressId: addr.id || null,
              },
              { emitEvent: false },
            );

            // Carica le opzioni geografiche in sequenza e poi popola i valori
            this.loadAddressDataSequentially(addr);
          }

          this.detailsForm.patchValue({
            codeAlboSOL: mappedSupplier.details.codeAlboSOL,
            codeSupplierCoupa: mappedSupplier.details.codeSupplierCoupa,
            coupaAuthor: mappedSupplier.details.coupaAuthor,
            codeSupplierBravoSolution: mappedSupplier.details.codeSupplierBravoSolution,
          });

          // Aggiorna breadcrumb con il nome del fornitore
          this.breadcrumbItems.set([
            { label: 'Registro Fornitori', routerLink: '/axa/suppliers-registry' },
            { label: mappedSupplier.supplierName || 'Dettaglio Fornitore' },
          ]);

          this.isLoading.set(false);

          // Imposta l'ID del fornitore per il componente contratti
          this.supplierId.set(id);
        } else {
          console.error('Invalid API response format:', response);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Formato dati non valido ricevuto dal server',
            life: 5000,
          });
          this.isLoading.set(false);
        }
      },
      error: (error) => {
        console.error('Error loading supplier details:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore nel recupero dei dati',
          detail: 'Impossibile caricare i dettagli del fornitore. Riprova più tardi.',
          life: 5000,
        });
        this.isLoading.set(false);
      },
    });
  }

  private getSupplierTypeLabel(typeId: number): string {
    // Map supplier type ID to label
    const types: Record<number, string> = {
      1: 'SRL - Società a Responsabilità Limitata',
      2: 'SPA - Società per Azioni',
      3: 'SRLS - Società a Responsabilità Limitata Semplificata',
      4: 'SNC - Società in Nome Collettivo',
      5: 'SAS - Società in Accomandita Semplice',
    };
    return types[typeId] || `Tipo ${typeId}`;
  }

  /**
   * Carica i dati dell'indirizzo in modo sequenziale per evitare problemi di sincronizzazione
   * Prima carica tutte le opzioni geografiche necessarie, poi popola i valori
   */
  private loadAddressDataSequentially(addr: any): void {
    // Per paesi diversi da IT, popola direttamente i valori
    if (addr.country !== 'IT') {
      this.anagraficaForm.patchValue(
        {
          addressTypology: addr.typology || '',
          address: addr.address || '',
          addressNumber: addr.number || '',
          zipCode: addr.zipCode || '',
          country: addr.country || '',
          region: addr.region || '',
          province: addr.province || '',
          municipality: addr.municipality || '',
        },
        { emitEvent: false },
      );
      return;
    }

    // Per l'Italia, carica le opzioni geografiche in sequenza
    if (addr.region) {
      this.geographyService.getRegions('IT').subscribe({
        next: (regions) => {
          this.regionOptions.set(regions);

          // Se c'è una provincia, caricala
          if (addr.province) {
            this.geographyService.getProvinces(addr.region).subscribe({
              next: (provinces) => {
                this.provinceOptions.set(provinces);

                // Se c'è un comune, caricalo
                if (addr.municipality) {
                  this.geographyService.getMunicipalities(addr.province).subscribe({
                    next: (municipalities) => {
                      this.municipalityOptions.set(municipalities);

                      // Ora che tutte le opzioni sono caricate, popola i valori
                      this.populateAddressFields(addr);
                    },
                    error: (err) => {
                      console.error('Error loading municipalities:', err);
                      // Popola comunque i campi anche in caso di errore
                      this.populateAddressFields(addr);
                    },
                  });
                } else {
                  // Nessun comune, popola i campi
                  this.populateAddressFields(addr);
                }
              },
              error: (err) => {
                console.error('Error loading provinces:', err);
                // Popola comunque i campi anche in caso di errore
                this.populateAddressFields(addr);
              },
            });
          } else {
            // Nessuna provincia, popola i campi
            this.populateAddressFields(addr);
          }
        },
        error: (err) => {
          console.error('Error loading regions:', err);
          // Popola comunque i campi anche in caso di errore
          this.populateAddressFields(addr);
        },
      });
    } else {
      // Nessuna regione, popola i campi
      this.populateAddressFields(addr);
    }
  }

  /**
   * Popola i campi dell'indirizzo nel form
   * Questo metodo viene chiamato solo dopo che tutte le opzioni geografiche sono state caricate
   */
  private populateAddressFields(addr: any): void {
    this.anagraficaForm.patchValue(
      {
        addressTypology: addr.typology || '',
        address: addr.address || '',
        addressNumber: addr.number || '',
        zipCode: addr.zipCode || '',
        country: addr.country || 'IT',
        region: addr.region || '',
        province: addr.province || '',
        municipality: addr.municipality || '',
      },
      { emitEvent: false },
    );
  }

  // Metodi per caricamento opzioni dropdown
  private loadDropdownOptions(): void {
    this.formDropdownService.getSupplierVatCountries().subscribe({
      next: options => {
        this.vatCountryOptions.set(options);
        this.anagraficaForm.get('vatCountry')?.updateValueAndValidity({ emitEvent: false });
      },
      error: err => {
        console.error('Error loading VAT countries:', err);
        this.anagraficaForm.get('vatCountry')?.setErrors({ vatCountriesUnavailable: true });
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare i paesi IVA. Ricarica la pagina prima di salvare.',
        });
      },
    });
    // Carica tipologie indirizzo
    this.formDropdownService.getFormDropdownValues('PLACE_NAME', 'PLACE_NAME').subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.addressTypologyOptions.set(response.data);
        }
      },
      error: (err) => {
        console.error('Error loading place names:', err);
      },
    });

    // Carica person type
    this.formDropdownService.getFormDropdownValues('PERSON_TYPE', 'PERSON_TYPE').subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.personTypeOptions.set(response.data);
        }
      },
      error: (err) => {
        console.error('Error loading person types:', err);
      },
    });

    // Carica supplier types
    this.formDropdownService.getFormDropdownValues('CREATE_SUPPLIER', 'SUPPLIER_TYPE').subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.allSupplierTypeOptions.set(response.data);
          this.supplierTypeOptions.set(response.data); // Inizialmente mostra tutti
        }
      },
      error: (err) => {
        console.error('Error loading supplier types:', err);
      },
    });
  }

  private loadCountries(): void {
    this.geographyService.getCountries().subscribe({
      next: (countries) => {
        this.countryOptions.set(countries);
      },
      error: (err) => {
        console.error('Error loading countries:', err);
      },
    });
  }

  private enableAnagraficaFields(): void {
    Object.keys(this.anagraficaForm.controls).forEach((key) => {
      this.anagraficaForm.get(key)?.enable({ emitEvent: false });
    });
  }

  private disableAnagraficaFields(): void {
    Object.keys(this.anagraficaForm.controls).forEach((key) => {
      this.anagraficaForm.get(key)?.disable({ emitEvent: false });
    });
  }

  private enableDetailsFields(): void {
    Object.keys(this.detailsForm.controls).forEach((key) => {
      this.detailsForm.get(key)?.enable({ emitEvent: false });
    });
  }

  private disableDetailsFields(): void {
    Object.keys(this.detailsForm.controls).forEach((key) => {
      this.detailsForm.get(key)?.disable({ emitEvent: false });
    });
  }

  private enableAddressFields(): void {
    [
      'addressTypology',
      'address',
      'addressNumber',
      'zipCode',
      'country',
      'region',
      'province',
      'municipality',
    ].forEach((field) => {
      this.anagraficaForm.get(field)?.enable({ emitEvent: false });
    });
  }

  private disableAddressFields(): void {
    [
      'addressTypology',
      'address',
      'addressNumber',
      'zipCode',
      'country',
      'region',
      'province',
      'municipality',
    ].forEach((field) => {
      this.anagraficaForm.get(field)?.disable({ emitEvent: false });
    });
  }

  // Setup listeners per cascata geografica
  private setupAddressListeners(): void {
    this.anagraficaForm.get('vatCountry')?.valueChanges.subscribe(() => {
      this.anagraficaForm.get('vatNumber')?.updateValueAndValidity({ emitEvent: false });
    });
    this.anagraficaForm.get('vatNumber')?.valueChanges.subscribe((vatNumber) => {
      if (this.isEditModeAnagrafica() &&
          this.anagraficaForm.get('personType')?.value === 'LEGAL_ENTITY' && vatNumber) {
        this.anagraficaForm.get('fiscalCode')?.setValue(vatNumber, { emitEvent: false });
      }
    });
    // Listener per personType - filtra i supplier types e aggiorna validazione vatNumber
    this.anagraficaForm.get('personType')?.valueChanges.subscribe((personType) => {
      this.filterSupplierTypes(personType);
      this.updateVatNumberValidation(personType);
    });

    this.anagraficaForm.get('country')?.valueChanges.subscribe((countryCode) => {
      this.onCountryChange(countryCode);
    });

    this.anagraficaForm.get('region')?.valueChanges.subscribe((regionCode) => {
      this.onRegionChange(regionCode);
    });

    this.anagraficaForm.get('province')?.valueChanges.subscribe((provinceCode) => {
      this.onProvinceChange(provinceCode);
    });
  }

  private updateVatNumberValidation(personType: string | null): void {
    const vatNumberControl = this.anagraficaForm.get('vatNumber');

    // Normalizza il valore di personType
    const personTypeUpper = personType?.toUpperCase() || '';
    const isPersonaGiuridica =
      personTypeUpper.includes('PG') ||
      personTypeUpper.includes('GIURIDICA') ||
      personTypeUpper.includes('LEGAL');

    if (isPersonaGiuridica) {
      // Persona Giuridica: Partita IVA obbligatoria
      vatNumberControl?.setValidators([
        Validators.required,
        SupplierValidatorsService.vatNumberValidator,
      ]);
    } else {
      // Persona Fisica: Partita IVA opzionale ma se inserita deve essere valida
      vatNumberControl?.setValidators([SupplierValidatorsService.vatNumberValidator]);
    }
    vatNumberControl?.updateValueAndValidity();
  }

  // Getter per verificare se vatNumber è required (usato nel template)
  isVatNumberRequired(): boolean {
    return this.anagraficaForm.get('vatNumber')?.hasValidator(Validators.required) ?? false;
  }

  private filterSupplierTypes(personType: string | null): void {
    if (!personType) {
      this.supplierTypeOptions.set(this.allSupplierTypeOptions());
      return;
    }

    const allTypes = this.allSupplierTypeOptions();

    // Se non ci sono opzioni caricate, non filtrare
    if (allTypes.length === 0) {
      console.log('No supplier types loaded yet');
      return;
    }

    console.log('Filtering supplier types for personType:', personType);
    console.log('All supplier types:', allTypes);

    const personTypeUpper = personType.toUpperCase();

    const filtered = allTypes.filter((type) => {
      const typeCode = type.valueKey?.toString().toUpperCase() || '';
      const desc = type.valueDescription?.toUpperCase() || '';

      // Persona Fisica - controlla vari pattern
      const isPersonaFisica =
        personTypeUpper.includes('PF') ||
        personTypeUpper.includes('FISICA') ||
        personTypeUpper.includes('INDIVIDUAL');

      // Persona Giuridica - controlla vari pattern
      const isPersonaGiuridica =
        personTypeUpper.includes('PG') ||
        personTypeUpper.includes('GIURIDICA') ||
        personTypeUpper.includes('LEGAL');

      if (isPersonaFisica) {
        // Solo tipi compatibili con Persona Fisica
        return (
          typeCode.includes('INDIVIDUAL') ||
          typeCode.includes('PF') ||
          desc.includes('INDIVIDUALE') ||
          desc.includes('PERSONA FISICA')
        );
      } else if (isPersonaGiuridica) {
        // Escludi tipi per persone fisiche
        const isIndividualType =
          typeCode.includes('INDIVIDUAL') ||
          typeCode.includes('PF') ||
          desc.includes('INDIVIDUALE') ||
          desc.includes('PERSONA FISICA');
        return !isIndividualType;
      }

      // Se non matcha nessun pattern, mostra tutto
      return true;
    });

    console.log('Filtered supplier types:', filtered);
    this.supplierTypeOptions.set(filtered);

    // Reset supplierTypeId se non è più valido nel filtro
    const currentTypeId = this.anagraficaForm.get('supplierTypeId')?.value;
    if (currentTypeId) {
      const stillValid = filtered.some((t) => t.valueKey === currentTypeId);
      if (!stillValid) {
        console.log('Current supplierTypeId is not valid, resetting');
        this.anagraficaForm.patchValue({ supplierTypeId: null }, { emitEvent: false });
      }
    }
  }

  private onCountryChange(countryCode: string | null | undefined): void {
    // Reset campi dipendenti
    this.anagraficaForm.patchValue(
      {
        region: '',
        province: '',
        municipality: '',
      },
      { emitEvent: false },
    );

    this.regionOptions.set([]);
    this.provinceOptions.set([]);
    this.municipalityOptions.set([]);

    // Carica regioni solo se il paese è Italia
    if (countryCode === 'IT') {
      this.geographyService.getRegions('IT').subscribe({
        next: (regions) => {
          this.regionOptions.set(regions);
        },
        error: (err) => {
          console.error('Error loading regions:', err);
        },
      });
    }
  }

  private onRegionChange(regionCode: string | null | undefined): void {
    // Reset campi dipendenti
    this.anagraficaForm.patchValue(
      {
        province: '',
        municipality: '',
      },
      { emitEvent: false },
    );

    this.provinceOptions.set([]);
    this.municipalityOptions.set([]);

    // Carica province solo se regione selezionata
    if (regionCode) {
      this.anagraficaForm.get('province')?.enable({ emitEvent: false });
      this.geographyService.getProvinces(regionCode).subscribe({
        next: (provinces) => {
          this.provinceOptions.set(provinces);
        },
        error: (err) => {
          console.error('Error loading provinces:', err);
        },
      });
    } else {
      this.anagraficaForm.get('province')?.disable({ emitEvent: false });
    }
  }

  private onProvinceChange(provinceCode: string | null | undefined): void {
    // Reset campi dipendenti
    this.anagraficaForm.patchValue(
      {
        municipality: '',
      },
      { emitEvent: false },
    );

    this.municipalityOptions.set([]);

    // Carica comuni solo se provincia selezionata
    if (provinceCode) {
      this.anagraficaForm.get('municipality')?.enable({ emitEvent: false });
      this.geographyService.getMunicipalities(provinceCode).subscribe({
        next: (municipalities) => {
          this.municipalityOptions.set(municipalities);
        },
        error: (err) => {
          console.error('Error loading municipalities:', err);
        },
      });
    } else {
      this.anagraficaForm.get('municipality')?.disable({ emitEvent: false });
    }
  }

  getStatusSeverity(status?: string): 'success' | 'secondary' | 'danger' | 'warn' | 'info' {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'DRAFT':
        return 'secondary';
      case 'INACTIVE':
        return 'secondary';
      default:
        return 'info';
    }
  }

  getStatusDisplay(status?: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'ATTIVO';
      case 'INACTIVE':
        return 'INATTIVO';
      case 'CEASED':
        return 'CESSATO';
      case 'DRAFT':
        return 'BOZZA';
      default:
        return status || '';
    }
  }

  // Toggle edit mode per sezioni separate
  toggleEditAnagrafica(): void {
    const wasEditMode = this.isEditModeAnagrafica();

    if (wasEditMode) {
      // Uscendo dalla modalità edit - mostra conferma
      this.confirmDialogService.confirmCancelEdit(() => {
        this.isEditModeAnagrafica.set(false);
        // Ricarica i dati originali
        const supplierId = this.supplierId();
        if (supplierId) {
          this.loadSupplierDetails(supplierId);
        }
      });
    } else {
      // Entra in modalità edit
      this.isEditModeAnagrafica.set(true);
    }
  }

  cancelEditAnagrafica(): void {
    // Mostra dialog di conferma prima di annullare
    this.confirmDialogService.confirmCancelEdit(() => {
      this.isEditModeAnagrafica.set(false);
      const supplierId = this.supplierId();
      if (supplierId) {
        this.loadSupplierDetails(supplierId);
      }
    });
  }

  toggleEditDetails(): void {
    const wasEditMode = this.isEditModeDetails();

    if (wasEditMode) {
      // Uscendo dalla modalità edit - mostra conferma
      this.confirmDialogService.confirmCancelEdit(() => {
        this.isEditModeDetails.set(false);
        // Ricarica i dati originali
        const supplierId = this.supplierId();
        if (supplierId) {
          this.loadSupplierDetails(supplierId);
        }
      });
    } else {
      // Entra in modalità edit
      this.isEditModeDetails.set(true);
    }
  }

  cancelEditDetails(): void {
    // Mostra dialog di conferma prima di annullare
    this.confirmDialogService.confirmCancelEdit(() => {
      this.isEditModeDetails.set(false);
      const supplierId = this.supplierId();
      if (supplierId) {
        this.loadSupplierDetails(supplierId);
      }
    });
  }

  private getAnagraficaFieldLabel(field: string): string {
    const labels: Record<string, string> = {
      supplierName: 'Ragione Sociale',
      supplierTypeId: 'Tipo Società',
      personType: 'Persona fisica / giuridica',
      vatCountry: 'Paese partita IVA',
      vatNumber: 'Partita IVA',
      fiscalCode: 'Codice Fiscale',
      primaryEmail: 'Email ordinaria',
      certifiedEmail: 'Email PEC',
      telephoneNumber: 'Telefono',
      mobileNumber: 'Mobile',
      axaBuyer: 'Buyer AXA',
    };

    return labels[field] || field;
  }

  private getAnagraficaFieldSuggestion(field: string, errors: ValidationErrors | null): string {
    if (!errors) {
      return 'Verifica il valore inserito.';
    }

    if (errors['required']) {
      return 'Compila il campo obbligatorio.';
    }

    if (errors['email'] || errors['invalidPecEmail']) {
      return 'Inserisci un indirizzo email valido (es. nome@dominio.it).';
    }

    if (errors['invalidVatNumber']) {
      return `${this.vatNumberHint}, senza prefisso paese.`;
    }

    if (errors['invalidVatCountry'] || errors['vatCountriesUnavailable']) {
      return 'Seleziona un paese IVA valido; se i paesi non sono disponibili, ricarica la pagina.';
    }

    if (errors['invalidTaxCode']) {
      return 'Inserisci un Codice Fiscale valido (16 caratteri) o 11 cifre numeriche.';
    }

    if (errors['invalidPhoneNumber']) {
      return 'Inserisci un numero valido (es. +390212345678 o 0212345678).';
    }

    if (errors['invalidMobileNumber']) {
      return 'Inserisci un cellulare valido (es. +393331234567 o 3331234567).';
    }

    return 'Controlla formato e contenuto del campo.';
  }

  private buildAnagraficaValidationDetail(): string {
    const invalidFields = Object.keys(this.anagraficaForm.controls)
      .filter((key) => this.anagraficaForm.get(key)?.invalid)
      .slice(0, 4)
      .map((key) => {
        const control = this.anagraficaForm.get(key);
        const label = this.getAnagraficaFieldLabel(key);
        const suggestion = this.getAnagraficaFieldSuggestion(key, control?.errors || null);
        return `${label}: ${suggestion}`;
      });

    const totalInvalid = Object.keys(this.anagraficaForm.controls).filter(
      (key) => this.anagraficaForm.get(key)?.invalid,
    ).length;

    const extraCount = totalInvalid - invalidFields.length;
    const extraText = extraCount > 0 ? ` Altri ${extraCount} campi da correggere.` : '';

    return `Campi da correggere: ${invalidFields.join(' | ')}.${extraText}`;
  }

  /**
   * Controlla se ci sono campi anagrafica con caratteri speciali non permessi
   * @returns Array di nomi campi con errori
   */
  private hasSpecialCharsErrorsAnagrafica(): string[] {
    const fieldsWithErrors: string[] = [];
    const fieldLabels: { [key: string]: string } = {
      supplierName: 'Nome Fornitore',
      alternativeName: 'Denominazione commerciale',
      vatNumber: 'Partita IVA',
      fiscalCode: 'Codice Fiscale',
      telephoneNumber: 'Telefono',
      mobileNumber: 'Cellulare',
      primaryEmail: 'Email Primaria',
      certifiedEmail: 'Email Certificata (PEC)',
      contactPerson: 'Persona di Contatto',
      axaBuyer: 'AXA Buyer',
      address: 'Indirizzo',
      addressNumber: 'Numero Civico',
      zipCode: 'CAP',
    };

    Object.keys(this.anagraficaForm.controls).forEach(key => {
      const control = this.anagraficaForm.get(key);
      if (control?.hasError('specialChars') && fieldLabels[key]) {
        fieldsWithErrors.push(fieldLabels[key]);
      }
    });

    return fieldsWithErrors;
  }

  /**
   * Controlla se ci sono campi dettagli con caratteri speciali non permessi
   * @returns Array di nomi campi con errori
   */
  private hasSpecialCharsErrorsDetails(): string[] {
    const fieldsWithErrors: string[] = [];
    const fieldLabels: { [key: string]: string } = {
      codeAlboSOL: 'Codice Albo SOL',
      codeSupplierCoupa: 'Codice Fornitore Coupa',
      coupaAuthor: 'Autore Coupa',
      codeSupplierBravoSolution: 'Codice Fornitore Bravo Solution',
    };

    Object.keys(this.detailsForm.controls).forEach(key => {
      const control = this.detailsForm.get(key);
      if (control?.hasError('specialChars') && fieldLabels[key]) {
        fieldsWithErrors.push(fieldLabels[key]);
      }
    });

    return fieldsWithErrors;
  }

  saveAnagrafica(): void {
    // ✅ XSS VALIDATION: Controlla caratteri speciali PRIMA della validazione required
    const specialCharsFields = this.hasSpecialCharsErrorsAnagrafica();
    if (specialCharsFields.length > 0) {
      Object.keys(this.anagraficaForm.controls).forEach((key) => {
        this.anagraficaForm.get(key)?.markAsTouched();
      });
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: `I seguenti campi contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggi i campi evidenziati.`,
        life: 6000,
      });
      return;
    }

    // Valida il form anagrafica prima del salvataggio
    if (this.anagraficaForm.invalid) {
      Object.keys(this.anagraficaForm.controls).forEach((key) => {
        this.anagraficaForm.get(key)?.markAsTouched();
      });
      this.messageService.add({
        severity: 'warn',
        summary: 'Validazione',
        detail: this.buildAnagraficaValidationDetail(),
        life: 5000,
      });
      return;
    }

    const currentSupplier = this.supplier();

    if (!currentSupplier.id) {
      console.error('Cannot save: supplier ID is missing');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID fornitore mancante. Impossibile salvare.',
        life: 3000,
      });
      return;
    }

    // Prepara payload anagrafica con indirizzo
    const anagraficaValues = this.anagraficaForm.value;

    // Verifica se i campi indirizzo sono popolati
    const hasAddressData =
      anagraficaValues.address ||
      anagraficaValues.addressNumber ||
      anagraficaValues.zipCode ||
      anagraficaValues.addressTypology ||
      anagraficaValues.region ||
      anagraficaValues.province ||
      anagraficaValues.municipality;

    const payload: any = {
      supplierName: anagraficaValues.supplierName,
      supplierTypeId: anagraficaValues.supplierTypeId,
      alternativeName: anagraficaValues.alternativeName,
      personType: anagraficaValues.personType,
      vatCountry: anagraficaValues.vatCountry,
      vatNumber: anagraficaValues.vatNumber,
      fiscalCode: anagraficaValues.fiscalCode,
      telephoneNumber: anagraficaValues.telephoneNumber,
      mobileNumber: anagraficaValues.mobileNumber,
      primaryEmail: anagraficaValues.primaryEmail,
      certifiedEmail: anagraficaValues.certifiedEmail,
      contactPerson: anagraficaValues.contactPerson,
      axaBuyer: anagraficaValues.axaBuyer,
    };

    // Aggiungi addresses solo se ci sono dati popolati
    if (hasAddressData) {
      payload.addresses = [
        {
          id: anagraficaValues.addressId, // Include l'ID dell'indirizzo esistente
          typology: anagraficaValues.addressTypology,
          address: anagraficaValues.address,
          number: anagraficaValues.addressNumber,
          zipCode: anagraficaValues.zipCode,
          country: anagraficaValues.country,
          region: anagraficaValues.region,
          province: anagraficaValues.province,
          municipality: anagraficaValues.municipality,
        },
      ];
    }

    const supplierId = currentSupplier.id.toString();

    this.supplierService.updateSupplierPut(supplierId, payload).subscribe({
      next: (response: any) => {
        console.log('Anagrafica saved successfully:', response);
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Anagrafica salvata con successo',
          life: 3000,
        });
        this.isEditModeAnagrafica.set(false);
        // Ricarica i dati per aggiornare la vista
        this.loadSupplierDetails(supplierId);
      },
      error: (error) => {
        console.error('Error saving anagrafica:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore nel salvataggio',
          detail: "Si è verificato un errore durante il salvataggio dell'anagrafica",
          life: 5000,
        });
      },
    });
  }

  saveDetails(): void {
    // ✅ XSS VALIDATION: Controlla caratteri speciali PRIMA della validazione required
    const specialCharsFields = this.hasSpecialCharsErrorsDetails();
    if (specialCharsFields.length > 0) {
      Object.keys(this.detailsForm.controls).forEach((key) => {
        this.detailsForm.get(key)?.markAsTouched();
      });
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: `I seguenti campi contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggi i campi evidenziati.`,
        life: 6000,
      });
      return;
    }

    // Valida il form dettagli prima del salvataggio
    if (this.detailsForm.invalid) {
      Object.keys(this.detailsForm.controls).forEach((key) => {
        this.detailsForm.get(key)?.markAsTouched();
      });
      this.messageService.add({
        severity: 'warn',
        summary: 'Validazione',
        detail: 'Correggi gli errori nei campi dettagli prima di salvare',
        life: 3000,
      });
      return;
    }

    const currentSupplier = this.supplier();

    if (!currentSupplier.id) {
      console.error('Cannot save: supplier ID is missing');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID fornitore mancante. Impossibile salvare.',
        life: 3000,
      });
      return;
    }

    // Prendi i valori dal form dettagli
    const detailsPayload = {
      details: this.detailsForm.value,
    };

    const supplierId = currentSupplier.id.toString();

    // Usa updateSupplierDetails per salvare i dettagli
    this.supplierService.updateSupplierDetails(supplierId, detailsPayload).subscribe({
      next: (response: any) => {
        console.log('Supplier details saved successfully:', response);
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Dettagli fornitore salvati con successo',
          life: 3000,
        });
        this.isEditModeDetails.set(false);
        // Ricarica i dati per aggiornare la vista
        this.loadSupplierDetails(supplierId);
      },
      error: (error: any) => {
        console.error('Error saving supplier details:', error);
        const errorMessage =
          error?.error?.message || 'Impossibile salvare i dettagli. Riprova più tardi.';
        this.messageService.add({
          severity: 'error',
          summary: 'Errore nel salvataggio',
          detail: errorMessage,
          life: 5000,
        });
      },
    });
  }

  goBack(): void {
    // Controlla se una delle sezioni è in modalità edit
    if (this.isEditModeAnagrafica() || this.isEditModeDetails()) {
      // Mostra dialog di conferma prima di navigare
      this.confirmDialogService.confirmNavigateToRegister('/axa/suppliers-registry');
    } else {
      // Nessuna modifica in corso, naviga direttamente
      this.router.navigate(['/axa/suppliers-registry']);
    }
  }

  // Metodo per il guard CanDeactivate - intercetta la navigazione
  canDeactivate(): Observable<boolean> | boolean {
    // Se non ci sono modifiche in corso, consenti la navigazione
    if (!this.isEditModeAnagrafica() && !this.isEditModeDetails()) {
      return true;
    }

    // Se ci sono modifiche, usa il servizio centralizzato per il dialog
    return new Observable<boolean>((observer) => {
      this.confirmDialogService.confirmCancelEdit(
        () => {
          // Utente ha confermato, esci dalla modalità edit e consenti la navigazione
          this.isEditModeAnagrafica.set(false);
          this.isEditModeDetails.set(false);
          observer.next(true);
          observer.complete();
        },
        () => {
          // Utente ha annullato, blocca la navigazione
          observer.next(false);
          observer.complete();
        },
      );
    });
  }
}
